import { useState, useEffect } from "react";
import getEnv from "../utils/getEnv";
import { getIsOnpremise } from "../utils";

export const useOnPremiseNetworkStatus = (): boolean => {
  const isOnpremise = getIsOnpremise();

  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    if (!isOnpremise) {
      return;
    }

    let checkInterval: NodeJS.Timeout | undefined;
    let lastCheck = 0;
    let mounted = true;

    const MIN_CHECK_GAP = 10000;
    const ACTIVE_CHECK_INTERVAL = 30000;
    const API_TIMEOUT = 3000;

    // Use Google by default, allow override via env variable

    const checkConnectivity = async () => {
      if (!mounted) return;

      const now = Date.now();
      if (now - lastCheck < MIN_CHECK_GAP) return;
      lastCheck = now;

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

        // Check external URL for real internet connectivity
        const CONNECTIVITY_CHECK_URL =
          getEnv("NEXT_PUBLIC_CONNECTIVITY_CHECK_URL") ||
          "https://www.google.com/generate_204";

        const response = await fetch(CONNECTIVITY_CHECK_URL, {
          method: "HEAD",
          mode: "no-cors",
          cache: "no-store",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // With no-cors, we can't read response status, but fetch will succeed if reachable
        if (mounted) {
          setIsOnline(true);
        }
      } catch (error) {
        if (mounted) {
          // Any error means we couldn't reach the external URL
          setIsOnline(false);
          console.warn("[NetworkStatus] Connectivity check failed:", error);
        }
      }
    };

    const startActiveChecking = () => {
      // Always clear any existing interval first to prevent memory leaks
      stopActiveChecking();

      if (!document.hidden && mounted) {
        checkInterval = setInterval(() => {
          if (!document.hidden && mounted) {
            checkConnectivity();
          }
        }, ACTIVE_CHECK_INTERVAL);
      }
    };

    const stopActiveChecking = () => {
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = undefined;
      }
    };

    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // Wait for immediate check to complete before starting periodic checks
        await checkConnectivity();
        startActiveChecking();
      } else {
        stopActiveChecking();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    checkConnectivity();
    startActiveChecking();

    return () => {
      mounted = false;
      stopActiveChecking();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isOnpremise]);

  return isOnline;
};

export default useOnPremiseNetworkStatus;
